'use client';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface DataPoint {
  name: string;
  [key: string]: string | number;
}

interface MetricLineChartProps {
  data: DataPoint[];
  lines: Array<{
    dataKey: string;
    name: string;
    color?: string;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  targetLine?: number;
  height?: number;
}

const BRAND_COLORS = {
  navy: '#0A1E2E',
  orange: '#E89841',
  cream: '#E8D5B7',
  blue: '#4A90E2',
  green: '#6FCF97',
  red: '#EB5757',
};

export default function MetricLineChart({
  data,
  lines,
  xAxisLabel,
  yAxisLabel,
  targetLine,
  height = 300,
}: MetricLineChartProps) {
  const defaultColors = [BRAND_COLORS.orange, BRAND_COLORS.blue, BRAND_COLORS.green];

  return (
    <div className="w-full my-6 p-4 bg-background/40 border border-border rounded-lg">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={BRAND_COLORS.cream + '20'} />
          <XAxis
            dataKey="name"
            stroke={BRAND_COLORS.cream}
            tick={{ fill: BRAND_COLORS.cream }}
            label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, fill: BRAND_COLORS.cream } : undefined}
          />
          <YAxis
            stroke={BRAND_COLORS.cream}
            tick={{ fill: BRAND_COLORS.cream }}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fill: BRAND_COLORS.cream } : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: BRAND_COLORS.navy,
              border: `1px solid ${BRAND_COLORS.orange}40`,
              borderRadius: '8px',
              color: BRAND_COLORS.cream,
            }}
            labelStyle={{ color: BRAND_COLORS.orange }}
          />
          <Legend
            wrapperStyle={{ color: BRAND_COLORS.cream }}
            iconType="line"
          />
          {lines.map((line, index) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color || defaultColors[index % defaultColors.length]}
              strokeWidth={2}
              dot={{ fill: line.color || defaultColors[index % defaultColors.length], r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
          {targetLine !== undefined && (
            <Line
              type="monotone"
              dataKey={() => targetLine}
              name="Target"
              stroke={BRAND_COLORS.green}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
